/**
 * Wire-mapping for the ephemeral-credentials family (OIDCPAM Gate-6 Phase 5d
 * — EphemeralCredsPage.tsx).
 *
 * Unlike several other PAM sub-areas migrated in this gate (BreakGlass,
 * EmergencyRoles, BrokeredSessions — see breakGlassMapping.ts /
 * brokeredSessionMapping.ts headers), the generated client's
 * AdminCredentialLeaseResp / AdminEphemeralCredResp / AdminRotateCredentialKeyResp
 * are NOT opaque `Record<string, unknown>` schemas — they are concretely
 * typed (see gen/client/react/client.ts) and the pre-migration page
 * (EphemeralCredsPage.tsx's toLease/toIssueResponse/toRotationResult) already
 * read the correct camelCase fields off them field-by-field. Verified against
 * the real client types; NO latent snake_case/camelCase bug found in this
 * suite (same "clean" class as the Phase-5a AccessRequests migration).
 *
 * This module maps those already-correct camelCase client responses to the
 * @devnw/cyber-iam/iam/EphemeralCredentials family's camelCase prop types
 * (field names already match 1:1 — the only transform needed is
 * pre-formatting the ISO date fields to display strings, since the library
 * never parses dates — see CredentialLease/IssueCredentialResponse/
 * KeyRotationResult's doc comments in @devnw/cyber-iam/iam/types).
 */
import type {
  AdminCredentialLeaseResp,
  AdminEphemeralCredResp,
  AdminRotateCredentialKeyResp,
} from '@gen/react/client'
import type {
  CredentialLease as IamCredentialLease,
  CredentialLeaseState,
  IssueCredentialResponse as IamIssueCredentialResponse,
  KeyRotationResult as IamKeyRotationResult,
} from '@devnw/cyber-iam/iam/types'

/** HOST-COMPUTED display formatting — the library never parses dates. */
function fmtDate(iso?: string): string | undefined {
  return iso ? new Date(iso).toLocaleString() : undefined
}

export function toIamCredentialLease(
  r: AdminCredentialLeaseResp
): IamCredentialLease {
  return {
    id: r.id,
    subjectId: r.subjectId,
    issuerType: r.issuerType,
    state: r.state as CredentialLeaseState,
    sessionId: r.sessionId,
    resourceId: r.resourceId,
    scope: r.scope,
    issuedAt: fmtDate(r.issuedAt),
    expiresAt: fmtDate(r.expiresAt),
    revokedAt: fmtDate(r.revokedAt),
  }
}

export function toIamIssueCredentialResponse(
  r: AdminEphemeralCredResp
): IamIssueCredentialResponse {
  return {
    leaseId: r.leaseId,
    issuerType: r.issuerType,
    // SECURITY: passed through only — never persisted/logged by the page.
    credentialMaterial: r.credentialMaterial,
    caPubKey: r.caPubKey,
    validUntil: fmtDate(r.validUntil),
  }
}

export function toIamKeyRotationResult(
  r: AdminRotateCredentialKeyResp
): IamKeyRotationResult {
  return {
    oldKeyId: r.oldKeyId,
    newKeyId: r.newKeyId,
    rotatedAt: fmtDate(r.rotatedAt),
  }
}
